import { useAutoDispatch } from '@api/useAutoDispatch'
import { useFormatter } from '@api/useFormatter'
import { BBDTag, Horizontal, Texto, useThemeContext, Vertical } from '@gravitate-js/excalibrr'
import { Checkbox, Menu, Select, Spin } from 'antd'
import type { CheckboxChangeEvent } from 'antd/lib'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useLockedSchedules } from '@hooks/useLockedSchedules'
import { useScheduleContext } from '@components/OrderDispatch/Contexts/ScheduleContext'
import { formatGridMenuItems, formatDriverList, findShiftIndexById, generateDriverScheduleData } from './util'
import type { ScheduleGridProps } from '../types'

const { Option } = Select

export function ScheduleGrid({ scheduleData, depots, selectedRun, loading }: ScheduleGridProps) {
  const [shiftIndex, setShiftIndex] = useState(0)
  const { lockedScheduleStream } = useLockedSchedules()
  const { selectedDepots, selectedDrivers, handleDepotChange, handleDriverChange } = useScheduleContext()
  const { useScheduleLock, useScheduleUnlock } = useAutoDispatch()
  const [selectedIds, setSelectedIds] = useState<{ [shiftIndex: string]: string[] }>({})
  const [lockedIds, setLockedIds] = useState<string[]>([])
  const [pendingUpdates, setPendingUpdates] = useState<string[]>([])
  const menuItems = useMemo(() => formatGridMenuItems(scheduleData, selectedIds), [scheduleData, selectedIds])
  const driversList = useMemo(() => formatDriverList(scheduleData, shiftIndex), [scheduleData, shiftIndex])
  const driversByDepot = useMemo(
    () => generateDriverScheduleData(scheduleData, shiftIndex, selectedDepots, selectedDrivers),
    [scheduleData, shiftIndex, selectedDrivers, selectedDepots]
  )

  const isAllSelected = useMemo(() => {
    const selected = selectedIds?.[shiftIndex]
    return driversByDepot?.every(([, drivers]) =>
      drivers.every(({ id }) => lockedIds?.includes(id) || selected?.includes(id))
    )
  }, [driversByDepot, selectedIds, shiftIndex, lockedIds])

  const onSelectAll = ({ target: { checked } }) => {
    const ids = driversByDepot
      .flatMap(([_, drivers]) => drivers.map((driver) => driver.id))
      .filter((id) => !lockedIds.includes(id))
    setSelectedIds({ ...selectedIds, [shiftIndex]: checked ? ids : [] })
    if (checked) useScheduleLock.mutate({ ids, run_id: selectedRun.id })
    else useScheduleUnlock.mutate({ ids, run_id: selectedRun.id })
  }

  async function onDriverCheck(checked: boolean, id: string) {
    const ids = checked
      ? [...(selectedIds?.[shiftIndex] || []), id]
      : selectedIds?.[shiftIndex]?.filter((selectedId) => selectedId !== id)

    setSelectedIds({ ...selectedIds, [shiftIndex]: ids })

    // Prevents websocket messages from setting values back to true after unselecting
    setPendingUpdates((prev) => Array.from(new Set([...prev, id])))
    if (checked) await useScheduleLock.mutateAsync({ ids: [id], run_id: selectedRun.id })
    else await useScheduleUnlock.mutateAsync({ ids: [id], run_id: selectedRun.id })
    setPendingUpdates((prev) => prev.filter((item) => item !== id))
  }

  // Handles locked schedule websocket messages
  useEffect(() => {
    if (!driversByDepot?.length || !selectedRun) return

    const lockedIds = lockedScheduleStream.filter((s) => s.run_id !== selectedRun.id).map((s) => s.id)
    const itemsToUpdate = lockedScheduleStream.filter((s) => s.run_id === selectedRun.id).map((s) => s.id)
    const newSelectedIds = { ...selectedIds }

    // Unselects items that are no longer in the schedule
    Object.keys(newSelectedIds).forEach((shiftIndex) => {
      newSelectedIds[shiftIndex] = newSelectedIds[shiftIndex].filter(
        (id) => itemsToUpdate.includes(id) || pendingUpdates.includes(id)
      )
    })

    // Process items that need updating
    itemsToUpdate.forEach((id) => {
      const shiftIndex = findShiftIndexById(id, scheduleData)
      if (shiftIndex === -1 || pendingUpdates.includes(id)) return
      const current = newSelectedIds[shiftIndex] || []

      if (lockedIds.includes(id)) {
        newSelectedIds[shiftIndex] = current.filter((d) => d !== id)
      } else if (!current.includes(id)) {
        newSelectedIds[shiftIndex] = [...current, id]
      }
    })

    setLockedIds(lockedIds)
    setSelectedIds(newSelectedIds)
  }, [lockedScheduleStream, driversByDepot])

  if (loading) {
    return (
      <Vertical verticalCenter horizontalCenter>
        <Spin size='large' />
      </Vertical>
    )
  }

  return (
    <Vertical>
      {/* Toolbar */}
      <Horizontal
        justifyContent='space-between'
        style={{ minHeight: '50px' }}
        verticalCenter
        className='pl-4 pr-4 bordered'
      >
        <Horizontal verticalCenter gap='2em'>
          <Texto category='h5' className='mr-4'>
            Available Drivers
          </Texto>
          <Horizontal verticalCenter className='mr-5'>
            <Checkbox checked={isAllSelected} onChange={onSelectAll} /> <Texto className='ml-2'>SELECT ALL</Texto>
          </Horizontal>
          <Select
            mode='multiple'
            allowClear
            style={{ overflowY: 'auto', width: '350px', maxHeight: '50px' }}
            value={selectedDrivers}
            placeholder='Search for Driver Names'
            onChange={handleDriverChange}
            options={driversList}
            showSearch
          />
          <Select
            mode='multiple'
            allowClear
            style={{ overflowY: 'auto', width: '350px' }}
            value={selectedDepots}
            placeholder='All Driver Depots'
            onChange={handleDepotChange}
          >
            {depots?.map((depot) => (
              <Option key={depot}>{depot}</Option>
            ))}
          </Select>
        </Horizontal>
      </Horizontal>
      <Horizontal style={{ minHeight: '40px' }} className='bg-1'>
        <Vertical flex={0.15} justifyContent='center' className='ml-4 mr-4'>
          <Texto category='h5'>Shift Selection:</Texto>
        </Vertical>
        <Menu
          onClick={(e) => setShiftIndex(Number(e.key))}
          selectedKeys={[shiftIndex.toString()]}
          mode='horizontal'
          items={menuItems}
          style={{ width: '100%', flex: 1 }}
        />
      </Horizontal>
      <div style={{ overflowY: 'auto' }}>
        {/* Scroll Container */}
        {driversByDepot?.map(([depot, drivers]) => {
          return (
            <Vertical key={depot} className='p-2' gap={8} height='auto'>
              <Texto className='ml-4 px-2' category='heading-small'>
                {depot}
              </Texto>
              <Horizontal style={{ flexWrap: 'wrap' }}>
                {drivers.map((schedule) => (
                  <DriverCard
                    key={schedule.id}
                    schedule={schedule}
                    isSelected={selectedIds?.[shiftIndex]?.includes(schedule.id)}
                    isLocked={lockedIds.includes(schedule.id)}
                    onDriverCheck={(e: CheckboxChangeEvent) => onDriverCheck(e.target.checked, schedule.id)}
                  />
                ))}
              </Horizontal>
            </Vertical>
          )
        })}
      </div>
    </Vertical>
  )
}

type DriverCardProps = {
  schedule: Gravitate.Schedule
  onDriverCheck: (e: CheckboxChangeEvent) => void
  isLocked?: boolean
  isSelected?: boolean
}

function DriverCard({ schedule, onDriverCheck, isLocked, isSelected }: DriverCardProps) {
  const { isDarkTheme } = useThemeContext()
  const { formatDistance } = useFormatter()
  const formattedUnit = formatDistance(null, true)?.toUpperCase()
  const hoursAssigned = parseFloat(String(schedule?.hours_assigned || '0'))?.toFixed(1)
  const hoursAvail = parseFloat(String(schedule?.hours_available))?.toFixed(1) // display driver info

  if (!schedule) return
  return (
    <div
      className='p-2 bordered bg-1'
      style={{
        width: '300px',
        height: '80px',
        border: isSelected && !isLocked && '2px solid var(--theme-color-2)',
        backgroundColor: isSelected && !isLocked && 'var(--theme-color-2-dim)',
      }}
    >
      <Horizontal className='p-2'>
        <Vertical flex={0.4}>{!isLocked && <Checkbox onChange={onDriverCheck} checked={isSelected} />}</Vertical>
        <Vertical flex={2} alignItems='start'>
          <Texto className='text-ellipsis' category='h5' style={{ maxWidth: '100%' }}>
            {schedule?.driver_name}
          </Texto>
          <Texto category='p2' style={{ whiteSpace: 'nowrap' }}>
            {moment(schedule.shift_start).format('h:m A')} - {moment(schedule.shift_end).format('h:m A')}
          </Texto>
        </Vertical>
        <Vertical alignItems='end' className='pl-2' flex={1}>
          <BBDTag success style={{ backgroundColor: 'var(--gray-100)', marginRight: 0 }} className='text-truncate'>
            {hoursAssigned} / {hoursAvail}
          </BBDTag>
          <Texto className='pt-1 pr-1 text-truncate'>
            {formattedUnit}: {schedule.loaded_miles} | {schedule.total_miles}
          </Texto>
        </Vertical>
      </Horizontal>
      <Horizontal verticalCenter horizontalCenter className='pr-1 pl-1 pb-2'>
        {schedule.trips.map((trip, index) => {
          return (
            <Vertical
              style={{
                backgroundColor:
                  trip.order_id && trip.locked
                    ? isDarkTheme
                      ? 'var(--theme-color-2-dim)'
                      : 'var(--theme-color-1)'
                    : trip.order_id
                      ? 'var(--theme-option)'
                      : 'var(--gray-200)',
                height: '8px',
                minWidth: '5px',
                borderLeft: index === 0 ? 'none' : '1px solid var(--gray-400)',
              }}
              key={`${schedule.id}-trip-${index}`}
              flex={1}
            />
          )
        })}
      </Horizontal>
    </div>
  )
}
